// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { useState } from 'react';
import { useEffect } from 'react';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { RouteType } from '@Types';
import { generateId } from '@Helpers/string';
import MispDrawer from '@Organisms/PlatformConfig/Misp/MispDrawer/MispDrawer';
import THTable from '@Molecules/THTable/THTable';
import { THForm } from '@Molecules/THForm/THForm';
import { MispConfigurationOutput, MispServerOutput, PermissionsEnum } from 'thehive-sdk';
import lodash from 'lodash';
import RouteBuilder from '@Services/RouteBuilder';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { StatusContext } from '@Context/StatusProvider';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { FormatDate } from '@Molecules/Format';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import useValidateForm from '@Hooks/useValidateForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useDrawer } from '@Hooks/useDrawer';
import Button from 'antd/lib/button';
import usePageTitle from '@Hooks/usePageTitle';

import './MispDetails.scss';

interface ServerMisp extends MispServerOutput {
    id: string;
}

const MispDetails = (): ReactElement => {
    const local = useLocalMessage();
    const { quotas, licenseCapabilities } = useContext(StatusContext);

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: true,
    });

    const [serversStarted, setServersStarted] = useState<any[]>([]);
    const [serversUpdated, setServersUpdated] = useState<any[]>([]);
    const [serversTouched, setServersTouched] = useState(false);
    const { visible, setVisible, data: dataServer, setData: setDataServer } = useDrawer<Record<string, any>>({});

    const mispCapabilities = useMemo<boolean>(
        () => Object.keys(licenseCapabilities.misp || {}).includes('misp'),
        [licenseCapabilities],
    );

    const { data } = useGenericQuery({
        fetchCallback: () => client.MispController.getConfig(),
        key: ['get-misp-config'],
    });

    const { data: dataStatus } = useGenericQuery({
        fetchCallback: () => client.MispController.getStatus(),
        key: ['get-misp-status'],
    });

    const [mispConfig, setMispConfig] = useState<MispConfigurationOutput>();

    const addServer = useCallback(
        (newProxy: Record<string, any>) => {
            setServersUpdated((state) => [newProxy, ...state]);
            setServersTouched(true);
            onFormValuesChange();
            setFieldsTouched(true);
        },
        [setServersUpdated, onFormValuesChange, setFieldsTouched],
    );

    const updateServer =
        (_id: string) =>
        (state: any): void => {
            const newListServers = serversUpdated.map((e) => (e.id === _id ? { ...state } : { ...e }));
            setServersUpdated(newListServers || []);
            setServersTouched(true);
        };

    const onRemoveHandler = (id: string) => () => {
        setServersUpdated((state) => state.filter((e) => e.id !== id));
        setServersTouched(true);
    };

    const getServerData = useCallback(
        (_id?: string) => {
            const tmpConfig = { id: generateId(), auth: { type: 'key' } };
            if (!_id) {
                return tmpConfig;
            }

            return serversUpdated.find(({ id }) => id === _id) || tmpConfig;
        },
        [serversUpdated],
    );

    const onResetForm = () => {
        setServersTouched(false);
        setFieldsTouched(false);
        const serversTreated = serversStarted.map((e) => ({ ...e, id: generateId() }));
        setServersUpdated(serversTreated);
        form.resetFields();
    };

    const onSubmitHandler = (state: any) => {
        const computeConfig = lodash.merge(mispConfig, state);
        const data = { ...computeConfig, servers: serversUpdated };
        client.MispController.edit(data)
            .then(() => {
                openMessage('success', local('configPlateform.message.misp.success'));
                setMispConfig(data);
                setServersStarted(lodash.cloneDeep(data?.servers));
                setServersTouched(false);
                setFieldsTouched(false);
            })
            .catch(handleNotificationError);
    };

    const columns = useMemo(
        () => [
            {
                title: <ColumnTitle>{local('configPlateform.column.nameServer')}</ColumnTitle>,
                className: 'misp-servers__name custom-table__cell',
                render: (_: any, data: ServerMisp): ReactElement => {
                    return (
                        <div className="misp-servers__name-title">
                            <THButton
                                type="link"
                                disabled={!mispCapabilities}
                                onClick={() => {
                                    setDataServer(data);
                                    setVisible(true);
                                }}
                            >
                                {data.name}
                            </THButton>
                            <span className="misp-servers__title-url">{data.url}</span>
                        </div>
                    );
                },
            },
            {
                title: <ColumnTitle>{local('configPlateform.column.dates')}</ColumnTitle>,
                className: 'misp-servers__date custom-table__cell',
                render: (_: any, data: ServerMisp) => {
                    return (
                        dataStatus?.status &&
                        dataStatus?.status[data.name] && (
                            <ul className="misp-title__status">
                                {dataStatus?.status[data.name].lastSync !== null ? (
                                    <li className="misp-title__status-item">
                                        <span className="misp-status__label">
                                            {local('configPlateform.column.date-last-sync')}
                                        </span>
                                        <FormatDate
                                            className="misp-status__date"
                                            date={dataStatus?.status[data.name].lastSync}
                                        />
                                    </li>
                                ) : null}
                                {dataStatus?.status[data.name].lastEvent !== null ? (
                                    <li className="misp-title__status-item">
                                        <span className="misp-status__label">
                                            {local('configPlateform.column.date-last-event')}
                                        </span>
                                        <FormatDate
                                            className="misp-status__date"
                                            date={dataStatus?.status[data.name].lastEvent}
                                        />
                                    </li>
                                ) : null}
                            </ul>
                        )
                    );
                },
            },
            {
                className: 'misp-servers__action custom-table__cell',
                render: (_: any, data: ServerMisp): ReactElement => (
                    <ActionsList
                        actionList={{
                            delete: {
                                label: local('general.delete'),
                                confirm: true,
                                message: local('configPlateform.action.confirmDelete'),
                                query: () => onRemoveHandler(data.id)(),
                                disabled: !mispCapabilities,
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ),
            },
        ],
        //eslint-disable-next-line
        [updateServer, getServerData, dataStatus],
    );

    useEffect(() => {
        const serversTreated = lodash.cloneDeep(serversStarted).map((e) => ({ ...e, id: generateId() }));
        setServersUpdated(serversTreated);
    }, [serversStarted]);

    useEffect(() => {
        if (data) {
            setMispConfig(data);
            setServersStarted(lodash.cloneDeep(data?.servers));
        }
    }, [data]);

    useEffect(() => {
        form.resetFields();
    }, [mispConfig]); //eslint-disable-line

    usePageTitle(local('page-title.admin.platform.misp'));

    return (
        <>
            <THLayout>
                <THLayout.Content className="misp-layout">
                    {mispCapabilities ? (
                        <THForm
                            form={form}
                            onFinish={onSubmitHandler}
                            autoComplete="off"
                            initialValues={mispConfig}
                            onValuesChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                        >
                            <THForm.Layout
                                footer={
                                    <FormSubmitButton
                                        warning
                                        onCancel={onResetForm}
                                        disabled={!isValid}
                                        hidden={(!fieldsTouched && !serversTouched) || !mispCapabilities}
                                    />
                                }
                            >
                                <div className="misp-details__basic-informations">
                                    <TitleSection>{local('configPlateform.section.principal')}</TitleSection>
                                    <THForm.Item
                                        name={['interval']}
                                        className="misp-input__duration  input-half"
                                        label={local('configPlateform.field.interval')}
                                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                                    >
                                        <THNumberSelect
                                            options={['seconds', 'minutes', 'hours']}
                                            disabled={!mispCapabilities}
                                        />
                                    </THForm.Item>
                                </div>

                                <div className="misp-details__servers">
                                    <TitleSection>
                                        <div>{local('configPlateform.section.servers')}</div>
                                        <MispDrawer
                                            data={getServerData()}
                                            onSubmit={addServer}
                                            mode="add"
                                            triggerElem={
                                                <Button
                                                    type="dashed"
                                                    disabled={!mispCapabilities || !quotas.misp?.valid}
                                                    icon={<ICONS.PlusIcon />}
                                                    shape="circle"
                                                    size="small"
                                                />
                                            }
                                        />
                                    </TitleSection>
                                    <div className="misp-servers__wrapper">
                                        <THTable dataSource={serversUpdated} rowKey="id" columns={columns} />
                                    </div>
                                </div>
                            </THForm.Layout>
                        </THForm>
                    ) : (
                        <UnavailableMessage type="capability" />
                    )}
                </THLayout.Content>
            </THLayout>
            {dataServer && visible && (
                <MispDrawer
                    data={getServerData(dataServer.id)}
                    onSubmit={updateServer(dataServer.id)}
                    visible={visible}
                    afterVisible={setVisible}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.misp');
export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.misp',
    },
    breadcrumb: { label: 'breadcrumb.global.misp' },
    content: <MispDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],
    space: ['admin'],
    exact: true,
};
